import { fromEngineError, invalidInput, unsupported } from './errors.js';
import { coerceValue, likePattern, quoteIdentifier } from './parse.js';
import { getColumn } from './schema.js';
export function selectSql(request) {
    const params = [];
    const projection = projectionSql(request);
    const where = whereSql(request.filters, params);
    const order = orderSql(request.order);
    let text = `SELECT ${projection} FROM ${quoteIdentifier(request.table.name)}${where}${order}`;
    if (request.limit !== null) {
        params.push(request.limit);
        text += ` LIMIT $${params.length}`;
    }
    if (request.offset !== null) {
        params.push(request.offset);
        text += ` OFFSET $${params.length}`;
    }
    return { text, params };
}
export function countSql(request) {
    const params = [];
    const where = whereSql(request.filters, params);
    return {
        text: `SELECT COUNT(*)::int AS count FROM ${quoteIdentifier(request.table.name)}${where}`,
        params,
    };
}
export function insertSql(request, body) {
    const rows = normalizeRows(body);
    if (rows.length === 0) {
        throw invalidInput('Insert payload is required for DemoPack inserts.');
    }
    const columnNames = request.insertColumns ?? uniqueKeys(rows);
    if (columnNames.length === 0) {
        throw invalidInput('Insert payload must include at least one column.');
    }
    const columns = columnNames.map((name) => {
        const column = getColumn(request.table, name);
        if (!column) {
            throw unsupported(`Unsupported ${request.table.name} fields: ${name}`);
        }
        return column;
    });
    const params = [];
    const valueGroups = [];
    for (const row of rows) {
        const placeholders = [];
        for (const column of columns) {
            if (Object.prototype.hasOwnProperty.call(row, column.name)) {
                params.push(normalizeWriteValue(column, row[column.name]));
                placeholders.push(`$${params.length}`);
            }
            else if (request.missingDefault) {
                placeholders.push('DEFAULT');
            }
            else {
                params.push(null);
                placeholders.push(`$${params.length}`);
            }
        }
        valueGroups.push(`(${placeholders.join(', ')})`);
    }
    const returning = request.returnRepresentation ? ` RETURNING ${projectionSql(request)}` : '';
    return {
        text: `INSERT INTO ${quoteIdentifier(request.table.name)} (${columns.map((column) => quoteIdentifier(column.name)).join(', ')}) VALUES ${valueGroups.join(', ')}${returning}`,
        params,
    };
}
export function updateSql(request, body) {
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
        throw invalidInput('Update payload must be a single plain object.');
    }
    const row = body;
    const assignments = [];
    const params = [];
    for (const [key, value] of Object.entries(row)) {
        const column = getColumn(request.table, key);
        if (!column) {
            throw unsupported(`Unsupported ${request.table.name} fields: ${key}`);
        }
        params.push(normalizeWriteValue(column, value));
        assignments.push(`${quoteIdentifier(column.name)} = $${params.length}`);
    }
    if (assignments.length === 0) {
        throw invalidInput('Update payload must include at least one column.');
    }
    const where = whereSql(request.filters, params);
    const returning = request.returnRepresentation ? ` RETURNING ${projectionSql(request)}` : '';
    return {
        text: `UPDATE ${quoteIdentifier(request.table.name)} SET ${assignments.join(', ')}${where}${returning}`,
        params,
    };
}
export function deleteSql(request) {
    const params = [];
    const where = whereSql(request.filters, params);
    const returning = request.returnRepresentation ? ` RETURNING ${projectionSql(request)}` : '';
    return {
        text: `DELETE FROM ${quoteIdentifier(request.table.name)}${where}${returning}`,
        params,
    };
}
export async function runQuery(db, text, params) {
    try {
        const result = await db.query(text, params);
        const rows = result.rows ?? [];
        return {
            rows,
            rowCount: typeof result.affectedRows === 'number' ? result.affectedRows : rows.length,
        };
    }
    catch (error) {
        throw fromEngineError(error);
    }
}
function projectionSql(request) {
    if (request.selectColumns === '*') {
        return request.table.columns.map((column) => quoteIdentifier(column.name)).join(', ');
    }
    if (request.selectColumns.length === 0) {
        return request.table.columns.map((column) => quoteIdentifier(column.name)).join(', ');
    }
    return request.selectColumns.map((column) => quoteIdentifier(column.name)).join(', ');
}
function whereSql(filters, params) {
    if (filters.length === 0) {
        return '';
    }
    const clauses = filters.map((filter) => {
        const identifier = quoteIdentifier(filter.column.name);
        if (filter.operator === 'is') {
            return `${identifier} IS NULL`;
        }
        if (filter.operator === 'in') {
            const values = Array.isArray(filter.value) ? filter.value : [];
            if (values.length === 0) {
                return 'FALSE';
            }
            const placeholders = values.map((value) => {
                params.push(value);
                return `$${params.length}`;
            });
            return `${identifier} IN (${placeholders.join(', ')})`;
        }
        if (filter.operator === 'like' || filter.operator === 'ilike') {
            params.push(likePattern(String(filter.value)));
            const sqlOp = filter.operator === 'like' ? 'LIKE' : 'ILIKE';
            return `${identifier} ${sqlOp} $${params.length} ESCAPE '\\'`;
        }
        params.push(filter.value);
        return `${identifier} ${sqlOperator(filter.operator)} $${params.length}`;
    });
    return ` WHERE ${clauses.join(' AND ')}`;
}
function orderSql(order) {
    if (order.length === 0) {
        return '';
    }
    const clauses = order.map((item) => {
        const direction = item.ascending ? 'ASC' : 'DESC';
        const nulls = item.nulls === 'first' ? ' NULLS FIRST' : item.nulls === 'last' ? ' NULLS LAST' : '';
        return `${quoteIdentifier(item.column.name)} ${direction}${nulls}`;
    });
    return ` ORDER BY ${clauses.join(', ')}`;
}
function sqlOperator(operator) {
    switch (operator) {
        case 'eq':
            return '=';
        case 'neq':
            return '<>';
        case 'gt':
            return '>';
        case 'gte':
            return '>=';
        case 'lt':
            return '<';
        case 'lte':
            return '<=';
        default:
            throw unsupported(`Unsupported DemoPack filter operator: ${operator}`);
    }
}
function normalizeRows(body) {
    const rows = Array.isArray(body) ? body : body ? [body] : [];
    return rows.map((row) => {
        if (!row || typeof row !== 'object' || Array.isArray(row)) {
            throw invalidInput('DemoPack insert rows must be plain objects.');
        }
        return row;
    });
}
function uniqueKeys(rows) {
    const keys = [];
    for (const row of rows) {
        for (const key of Object.keys(row)) {
            if (!keys.includes(key)) {
                keys.push(key);
            }
        }
    }
    return keys;
}
function normalizeWriteValue(column, value) {
    if (value === null) {
        return null;
    }
    if (column.type === 'integer') {
        if (typeof value === 'number' && Number.isSafeInteger(value)) {
            return value;
        }
        if (typeof value === 'string') {
            return coerceValue(column, value);
        }
        throw invalidInput(`Expected integer value for ${column.name}.`);
    }
    if (typeof value !== 'string') {
        throw invalidInput(`${column.name} must be a string.`);
    }
    return value;
}
export function tableHasColumn(table, name) {
    return Boolean(getColumn(table, name));
}
